import { Plus, X } from "lucide-react";
import { useEffect, useRef, useState, type DragEvent as ReactDragEvent } from "react";
import {
  DocumentTabContextMenu,
  type DocumentTabContextAction,
} from "@/editor/shell/DocumentTabContextMenu";
import { documentDisplayName } from "@/editor/shell/shellTransfer";
import { documentStore, workspaceStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";

/** Tab strip height (32px). Stacked with TopBar `h-12` → panels use `top-20`. */
export const DOCUMENT_TAB_STRIP_CLASS = "h-8";

const TAB_DRAG_MIME = "application/x-curva-document-tab";

export interface DocumentTabStripProps {
  onActivate: (sessionId: string) => void;
  onClose: (sessionId: string) => void;
  onCloseMany: (sessionIds: string[]) => void;
  onNew: () => void;
  onRename: (sessionId: string, name: string) => void;
  /** Fired after a successful tab drag-reorder (desktop session persistence). */
  onSessionsReordered?: () => void;
}

export function DocumentTabStrip({
  onActivate,
  onClose,
  onCloseMany,
  onNew,
  onRename,
  onSessionsReordered,
}: DocumentTabStripProps) {
  const workspace = useStoreSubscribe(workspaceStore.state);
  const liveDocument = useStoreSubscribe(documentStore.document);
  const [editingSessionId, setEditingSessionId] = useState<string | undefined>();
  const [dragFromIndex, setDragFromIndex] = useState<number | undefined>();
  const [dropIndex, setDropIndex] = useState<number | undefined>();
  const [contextMenu, setContextMenu] = useState<{ sessionId: string; x: number; y: number } | undefined>();
  const skipClickRef = useRef(false);

  useEffect(() => {
    if (editingSessionId && !workspace.sessions.some(session => session.id === editingSessionId)) {
      setEditingSessionId(undefined);
      return;
    }
    if (editingSessionId && editingSessionId !== workspace.activeSessionId) {
      setEditingSessionId(undefined);
    }
  }, [editingSessionId, workspace.activeSessionId, workspace.sessions]);

  function beginRename(sessionId: string): void {
    if (sessionId !== workspace.activeSessionId) {
      onActivate(sessionId);
    }
    setEditingSessionId(sessionId);
  }

  function commitRename(sessionId: string, draft: string): void {
    setEditingSessionId(undefined);
    // Tab switch while editing unmounts the input (blur) — don't rename a background tab.
    if (workspaceStore.getState().activeSessionId !== sessionId) {
      return;
    }
    onRename(sessionId, draft);
  }

  function handleContextAction(sessionId: string, action: DocumentTabContextAction): void {
    const index = workspace.sessions.findIndex(session => session.id === sessionId);
    if (index < 0) {
      return;
    }
    switch (action) {
      case "newDocument":
        onNew();
        break;
      case "rename":
        beginRename(sessionId);
        break;
      case "close":
        onClose(sessionId);
        break;
      case "closeOthers":
        onCloseMany(workspace.sessions.filter(session => session.id !== sessionId).map(session => session.id));
        break;
      case "closeLeft":
        onCloseMany(workspace.sessions.slice(0, index).map(session => session.id));
        break;
      case "closeRight":
        onCloseMany(workspace.sessions.slice(index + 1).map(session => session.id));
        break;
      case "closeAll":
        onCloseMany(workspace.sessions.map(session => session.id));
        break;
    }
  }

  function dropTargetIndex(event: ReactDragEvent<HTMLElement>, tabIndex: number): number {
    const rect = event.currentTarget.getBoundingClientRect();
    const after = event.clientX > rect.left + rect.width / 2;
    return after ? tabIndex + 1 : tabIndex;
  }

  function applyReorder(fromIndex: number, insertIndex: number): void {
    let toIndex = insertIndex;
    if (fromIndex < insertIndex) {
      toIndex = insertIndex - 1;
    }
    workspaceStore.reorderSessions(fromIndex, toIndex);
    onSessionsReordered?.();
  }

  const contextSessionIndex = contextMenu
    ? workspace.sessions.findIndex(session => session.id === contextMenu.sessionId)
    : -1;

  return (
    <div
      className={`flex ${DOCUMENT_TAB_STRIP_CLASS} shrink-0 items-stretch gap-px border-b border-white/10 bg-[#191d22] px-1 text-[11px] text-slate-400`}
      role="tablist"
      aria-label="Documents"
    >
      <div className="flex min-w-0 flex-1 items-stretch gap-px overflow-x-auto">
        {workspace.sessions.map((session, index) => {
          const active = session.id === workspace.activeSessionId;
          const name = documentDisplayName(active ? liveDocument.name : session.document.name);
          const revision = active ? liveDocument.revision : session.document.revision;
          const dirty = session.history.getState(revision).dirtyRevision > 0;
          const editing = editingSessionId === session.id;
          const showDropBefore = dropIndex === index && dragFromIndex !== undefined && dragFromIndex !== index;
          const showDropAfter = dropIndex === index + 1
            && dragFromIndex !== undefined
            && dragFromIndex !== index
            && index === workspace.sessions.length - 1;

          return (
            <div
              key={session.id}
              role="tab"
              aria-selected={active}
              title={session.path ?? name}
              draggable={!editing}
              className={`group relative flex max-w-[11rem] min-w-[5.5rem] shrink-0 items-center gap-1.5 border-b-2 px-2.5 ${
                active
                  ? "border-sky-400 bg-[#1f2329] text-slate-100"
                  : "border-transparent hover:bg-white/[0.04] hover:text-slate-200"
              } ${dragFromIndex === index ? "opacity-45" : ""}`}
              onClick={() => {
                if (skipClickRef.current) {
                  skipClickRef.current = false;
                  return;
                }
                if (!active && !editing) {
                  onActivate(session.id);
                }
              }}
              onDoubleClick={event => {
                event.preventDefault();
                beginRename(session.id);
              }}
              onContextMenu={event => {
                event.preventDefault();
                setContextMenu({ sessionId: session.id, x: event.clientX, y: event.clientY });
              }}
              onAuxClick={event => {
                if (event.button === 1) {
                  event.preventDefault();
                  onClose(session.id);
                }
              }}
              onDragStart={event => {
                if (editing) {
                  event.preventDefault();
                  return;
                }
                event.dataTransfer.setData(TAB_DRAG_MIME, String(index));
                event.dataTransfer.effectAllowed = "move";
                setDragFromIndex(index);
                skipClickRef.current = true;
              }}
              onDragEnd={() => {
                setDragFromIndex(undefined);
                setDropIndex(undefined);
              }}
              onDragOver={event => {
                if (!event.dataTransfer.types.includes(TAB_DRAG_MIME)) {
                  return;
                }
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDropIndex(dropTargetIndex(event, index));
              }}
              onDragLeave={event => {
                const next = event.relatedTarget;
                if (next instanceof Node && event.currentTarget.contains(next)) {
                  return;
                }
              }}
              onDrop={event => {
                event.preventDefault();
                const fromIndex = Number.parseInt(event.dataTransfer.getData(TAB_DRAG_MIME), 10);
                const insertIndex = dropTargetIndex(event, index);
                setDragFromIndex(undefined);
                setDropIndex(undefined);
                if (Number.isInteger(fromIndex)) {
                  applyReorder(fromIndex, insertIndex);
                }
              }}
            >
              {showDropBefore ? <DropLine side="left" /> : null}
              {showDropAfter ? <DropLine side="right" /> : null}
              {editing ? (
                <TabNameInput
                  name={name}
                  onCancel={() => setEditingSessionId(undefined)}
                  onCommit={draft => commitRename(session.id, draft)}
                />
              ) : (
                <span className="min-w-0 flex-1 truncate">{name}</span>
              )}
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${dirty ? "bg-amber-400" : "bg-transparent"}`}
                aria-hidden={!dirty}
                title={dirty ? "Unsaved changes" : undefined}
              />
              <button
                type="button"
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-white/10 hover:text-slate-200 focus-visible:opacity-100 ${
                  active ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
                aria-label={`Close ${name}`}
                onClick={event => {
                  event.stopPropagation();
                  onClose(session.id);
                }}
                onMouseDown={event => event.stopPropagation()}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="flex h-full w-8 shrink-0 items-center justify-center text-slate-500 hover:bg-white/[0.06] hover:text-slate-200"
        aria-label="New document"
        onClick={onNew}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
      {contextMenu && contextSessionIndex >= 0 ? (
        <DocumentTabContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          canCloseOthers={workspace.sessions.length > 1}
          canCloseLeft={contextSessionIndex > 0}
          canCloseRight={contextSessionIndex < workspace.sessions.length - 1}
          onAction={action => handleContextAction(contextMenu.sessionId, action)}
          onClose={() => setContextMenu(undefined)}
        />
      ) : null}
    </div>
  );
}

function DropLine({ side }: { side: "left" | "right" }) {
  return (
    <span
      className={`pointer-events-none absolute bottom-1 top-1 w-0.5 rounded-full bg-sky-400 ${
        side === "left" ? "left-0" : "right-0"
      }`}
      aria-hidden
    />
  );
}

function TabNameInput({
  name,
  onCommit,
  onCancel,
}: {
  name: string;
  onCommit: (draft: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(name);
  const cancelledRef = useRef(false);

  return (
    <input
      className="min-w-0 flex-1 rounded-sm border border-sky-400/70 bg-[#0f1318] px-1 py-0.5 text-[11px] text-slate-50 outline-none"
      value={draft}
      aria-label="Document name"
      autoFocus
      onFocus={event => event.currentTarget.select()}
      onChange={event => setDraft(event.target.value)}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onBlur={() => {
        if (cancelledRef.current) {
          cancelledRef.current = false;
          return;
        }
        onCommit(draft);
      }}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(draft);
        } else if (event.key === "Escape") {
          event.preventDefault();
          cancelledRef.current = true;
          onCancel();
        }
      }}
    />
  );
}
